import { motion } from "framer-motion";
import { Twitter, Linkedin, Instagram, Facebook, Mail, Phone, MapPin, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");

  const links = {
    services: [
      { label: "ChatGPT Ads", href: "#services" },
      { label: "Gemini Ads", href: "#services" },
      { label: "Perplexity Ads", href: "#services" },
      { label: "AI Website Design", href: "#services" },
    ],
    company: [
      { label: "How It Works", href: "#how-it-works" },
      { label: "Industries", href: "#industries" },
      { label: "Blog", href: "/blog", isRoute: true },
      { label: "FAQ", href: "#faq" },
      { label: "Contact", href: "#contact" },
    ],
    legal: [
      { label: "Privacy Policy", href: "/privacy-policy", isRoute: true },
      { label: "Terms of Service", href: "/terms-of-service", isRoute: true },
      { label: "Refund Policy", href: "/refund-policy", isRoute: true },
      { label: "Disclaimer", href: "/disclaimer", isRoute: true },
    ],
  };

  const socials = [
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: Facebook, href: "#", label: "Facebook" },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/50" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      {/* CTA Section */}
      <div className="relative border-t border-border">
        <div className="container mx-auto px-4 py-16 max-w-[1280px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-gradient-to-br from-primary/10 via-card to-accent/10 border border-primary/20 rounded-3xl p-8 md:p-12 text-center overflow-hidden"
          >
            {/* Decorative elements */}
            <div className="absolute top-4 right-4 w-20 h-20 bg-primary/20 rounded-full blur-2xl" />
            <div className="absolute bottom-4 left-4 w-16 h-16 bg-accent/20 rounded-full blur-2xl" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Get Started Today
              </div>
              
              <h3 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
                Ready to Dominate AI Search?
              </h3>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                Join forward-thinking businesses already preparing for the AI advertising revolution.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 w-full px-5 py-3.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
                <button className="w-full sm:w-auto px-6 py-3.5 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 transition-all hover:scale-105 flex items-center justify-center gap-2 uppercase tracking-wide text-sm">
                  Get Free Audit
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative border-t border-border/50">
        <div className="container mx-auto px-4 py-12 md:py-16 max-w-[1280px]">
          <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-12">
            {/* Brand - Larger column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5"
            >
              <Link to="/" className="inline-block mb-5">
                <span className="text-2xl font-bold">
                  <span className="text-accent">AI Ads</span>
                  <span className="text-foreground"> Flow</span>
                </span>
              </Link>
              <p className="text-muted-foreground mb-6 leading-relaxed max-w-sm">
                The future of local business advertising. Get discovered where AI searches first—ChatGPT, Perplexity, Gemini, and beyond.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <a href="mailto:support@aiadsflow.com" className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors group">
                  <span className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Mail className="h-4 w-4 text-primary" />
                  </span>
                  <span>support@aiadsflow.com</span>
                </a>
                <a href="tel:+14075452129" className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors group">
                  <span className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                    <Phone className="h-4 w-4 text-accent" />
                  </span>
                  <span>(407) 545-2129</span>
                </a>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-foreground" />
                  </span>
                  <span>Serving USA & Canada</span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex gap-2">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="w-11 h-11 rounded-xl bg-secondary/50 border border-border/50 flex items-center justify-center hover:bg-primary hover:border-primary hover:text-primary-foreground transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                  >
                    <social.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-2"
            >
              <h4 className="font-bold text-foreground mb-5 text-sm uppercase tracking-wider">Services</h4>
              <ul className="space-y-3">
                {links.services.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 group"
                    >
                      <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Company */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h4 className="font-bold text-foreground mb-5 text-sm uppercase tracking-wider">Company</h4>
              <ul className="space-y-3">
                {links.company.map((link) => (
                  <li key={link.label}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 group"
                      >
                        <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 group"
                      >
                        <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Legal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="lg:col-span-3"
            >
              <h4 className="font-bold text-foreground mb-5 text-sm uppercase tracking-wider">Legal</h4>
              <ul className="space-y-3">
                {links.legal.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 group"
                    >
                      <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-border/50 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} AI Ads Flow. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                All systems operational
              </span>
              <span>Built for the future of AI search</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
